import type { PopperProps } from "@mui/base/Popper";
import type { ReactNode } from "react";
import React, { useMemo, useRef, useState } from "react";
import { useCopyToClipboard } from "react-use"; // TODO: wait for the mr merged https://github.com/react-hookz/web/pull/883/files#diff-0db06a79ebb52478c3ff555b5441ced52d7695b2523c22ee663b4d6cfb1e6249

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { Button, StyledIconTick, StyledPopper, Tooltip } from "./styles";

export type Props = {
  children?: ReactNode;
  className?: string;
  disablePortal?: boolean;
  LabelComponent: React.FunctionComponent<{ children: ReactNode }>;
  onClick?: () => void;
  showLabel?: boolean;
  tooltipPlacement?: PopperProps["placement"];
  url: string;
  variant: "label" | "responsive" | "tooltip";
};

export const CopyLinkButton: React.FunctionComponent<Props> = ({
  children,
  className,
  disablePortal = true,
  LabelComponent,
  onClick,
  showLabel,
  tooltipPlacement = "right",
  url,
  variant,
}) => {
  const [_, copyToClipboard] = useCopyToClipboard();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const tooltipAnchorReference = useRef<HTMLButtonElement>(null);
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"));

  const handleClick = () => {
    copyToClipboard(url);

    const handleTooltipAction = () => {
      setIsTooltipOpen(true);
      setTimeout(() => {
        setIsTooltipOpen(false);
        onClick?.();
      }, 1000);
    };

    const handleLabelAction = () => {
      copyToClipboard(url);
      setHasCopied(true);
      onClick?.();
    };

    switch (variant) {
      case "label":
        handleLabelAction();
        break;
      case "responsive":
        if (isTabletUp) {
          handleTooltipAction();
        } else {
          handleLabelAction();
        }
        break;
      case "tooltip":
        handleTooltipAction();
        break;
    }
  };

  const renderTooltipValue = () => {
    const copy = <span className="copy">Copy Link</span>;
    const copied = (
      <span className="copied">
        Link Copied <StyledIconTick />
      </span>
    );

    const labelVariantValue = hasCopied ? copied : copy;

    switch (variant) {
      case "label":
        return labelVariantValue;
      case "responsive":
        return isTabletUp ? copy : labelVariantValue;
      case "tooltip":
        return copy;
    }
  };

  const popperModifiers = useMemo<PopperProps["modifiers"]>(
    () => [
      {
        name: "offset",
        options: {
          offset: [0, 12],
        },
      },
    ],
    [],
  );

  return (
    <>
      <Button
        aria-label="copy-link"
        className={className}
        onClick={() => handleClick()}
        ref={tooltipAnchorReference}
        type="button"
      >
        {children}
        {showLabel && <LabelComponent>{renderTooltipValue()}</LabelComponent>}
      </Button>
      <StyledPopper
        anchorEl={tooltipAnchorReference.current}
        disablePortal={disablePortal}
        modifiers={popperModifiers}
        open={isTooltipOpen}
        placement={tooltipPlacement}
      >
        <Tooltip>
          Link Copied
          <StyledIconTick />
        </Tooltip>
      </StyledPopper>
    </>
  );
};
CopyLinkButton.displayName = "CopyLinkButton";
